import { useEffect, useMemo, useState } from "react";
import L from "leaflet";
import {
  CircleMarker,
  MapContainer,
  Marker,
  TileLayer,
  Tooltip,
  useMap,
  useMapEvents,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";

const DEFAULT_MAP_CENTER = { latitude: -27.4692, longitude: -58.8306 };

const normalizeLocation = (location) => {
  const latitude = Number(location?.latitude);
  const longitude = Number(location?.longitude);
  return Number.isFinite(latitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    Number.isFinite(longitude) &&
    longitude >= -180 &&
    longitude <= 180
    ? { latitude, longitude }
    : null;
};

function MapSelectionEvents({ onSelect }) {
  useMapEvents({
    click(event) {
      onSelect({
        latitude: event.latlng.lat,
        longitude: event.latlng.lng,
      });
    },
  });
  return null;
}

function RecenterMap({ location }) {
  const map = useMap();

  useEffect(() => {
    if (location) {
      map.setView([location.latitude, location.longitude], Math.max(map.getZoom(), 16));
    }
  }, [location, map]);

  return null;
}

function DeliveryLocationPicker({
  open,
  initialLocation,
  restaurantLocation,
  onClose,
  onConfirm,
  onLocateCurrent,
  resolving,
}) {
  const initialLatitude = initialLocation?.latitude;
  const initialLongitude = initialLocation?.longitude;
  const savedLocation = useMemo(
    () =>
      normalizeLocation({
        latitude: initialLatitude,
        longitude: initialLongitude,
      }),
    [initialLatitude, initialLongitude]
  );
  const restaurant = normalizeLocation(restaurantLocation);
  const [selectedLocation, setSelectedLocation] = useState(savedLocation);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState("");
  const center = selectedLocation || restaurant || DEFAULT_MAP_CENTER;
  const markerIcon = useMemo(
    () =>
      L.divIcon({
        className: "delivery-map-marker",
        html: '<span class="delivery-map-marker-pin"></span>',
        iconSize: [34, 42],
        iconAnchor: [17, 40],
      }),
    []
  );

  useEffect(() => {
    if (!open) return;
    setSelectedLocation(savedLocation);
    setLocationError("");
  }, [open, savedLocation]);

  if (!open) return null;

  const handleCurrentLocation = async () => {
    setLocating(true);
    setLocationError("");
    try {
      const location = normalizeLocation(await onLocateCurrent());
      if (!location) {
        throw new Error("No pudimos leer una ubicacion valida.");
      }
      setSelectedLocation(location);
    } catch (error) {
      setLocationError(error?.message || "No pudimos acceder a tu ubicacion.");
    } finally {
      setLocating(false);
    }
  };

  const markerEvents = {
    dragend(event) {
      const marker = event.target;
      const position = marker.getLatLng();
      setSelectedLocation({ latitude: position.lat, longitude: position.lng });
    },
  };

  return (
    <div className="delivery-location-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="delivery-location-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delivery-location-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="delivery-location-header">
          <div>
            <p className="delivery-public-eyebrow">Ubicacion de entrega</p>
            <h2 id="delivery-location-title">Marca la entrada de tu domicilio</h2>
            <span>Toca el mapa o arrastra el marcador hasta el punto exacto.</span>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar mapa">
            X
          </button>
        </header>

        <div className="delivery-location-map">
          <MapContainer
            center={[center.latitude, center.longitude]}
            zoom={selectedLocation ? 17 : restaurant ? 14 : 13}
            scrollWheelZoom
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapSelectionEvents onSelect={setSelectedLocation} />
            <RecenterMap location={selectedLocation} />
            {restaurant ? (
              <CircleMarker
                center={[restaurant.latitude, restaurant.longitude]}
                radius={7}
                pathOptions={{ color: "#0f172a", fillColor: "#ffffff", fillOpacity: 1 }}
              >
                <Tooltip direction="top">Restaurante</Tooltip>
              </CircleMarker>
            ) : null}
            {selectedLocation ? (
              <Marker
                position={[selectedLocation.latitude, selectedLocation.longitude]}
                icon={markerIcon}
                draggable
                eventHandlers={markerEvents}
              />
            ) : null}
          </MapContainer>
        </div>

        {locationError ? <p className="delivery-location-error">{locationError}</p> : null}

        <footer className="delivery-location-footer">
          <button
            type="button"
            className="delivery-secondary-button"
            onClick={handleCurrentLocation}
            disabled={locating || resolving}
          >
            {locating ? "Buscando ubicacion..." : "Usar mi ubicacion"}
          </button>
          <div>
            <button type="button" className="delivery-secondary-button" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="button"
              className="delivery-primary-button"
              disabled={!selectedLocation || resolving}
              onClick={() => onConfirm(selectedLocation)}
            >
              {resolving ? "Confirmando..." : "Confirmar punto"}
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
}

export default DeliveryLocationPicker;
